<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useResizeObserver } from '@vueuse/core';

const props = defineProps({
  initialActiveTab: {
    type: Number,
    default: 0,
  },
  tabs: {
    type: Array,
    required: true,
    validator: value => {
      return value.every(
        tab =>
          typeof tab.label === 'string' &&
          (tab.count ? typeof tab.count === 'number' : true)
      );
    },
  },
  ariaLabel: {
    type: String,
    default: 'Abas',
  },
  idBase: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['tabChanged']);

const generatedIdBase = `tabbar-${Math.random().toString(36).slice(2, 8)}`;
const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

const resolvedIdBase = computed(() => props.idBase || generatedIdBase);
const enabledIndexes = computed(() =>
  props.tabs
    .map((tab, index) => ({ tab, index }))
    .filter(({ tab }) => !tab.disabled)
    .map(({ index }) => index)
);

const activeTab = computed(() => {
  const requestedIndex = Number(props.initialActiveTab);
  if (
    Number.isInteger(requestedIndex) &&
    props.tabs[requestedIndex] &&
    !props.tabs[requestedIndex].disabled
  ) {
    return requestedIndex;
  }

  return enabledIndexes.value[0] ?? 0;
});

const activeElement = computed(() => tabRefs.value[activeTab.value]);

const updateIndicator = () => {
  nextTick(() => {
    if (!activeElement.value) return;

    indicatorStyle.value = {
      left: `${activeElement.value.offsetLeft}px`,
      width: `${activeElement.value.offsetWidth}px`,
    };
  });
};

useResizeObserver(activeElement, updateIndicator);

// Watch for prop/tabs changes to update indicator position
watch([() => props.initialActiveTab, () => props.tabs], updateIndicator, {
  immediate: true,
});

onMounted(() => {
  nextTick(() => {
    enableTransition.value = true;
  });
});

const domIdPart = value =>
  String(value)
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'tab';

const tabKey = (tab, index) =>
  tab.id ?? tab.key ?? tab.value ?? tab.label ?? index;

const tabId = (tab, index) =>
  `${resolvedIdBase.value}-tab-${domIdPart(tabKey(tab, index))}`;

const panelId = (tab, index) =>
  `${resolvedIdBase.value}-panel-${domIdPart(tabKey(tab, index))}`;

const focusTab = index => {
  nextTick(() => {
    const el = tabRefs.value[index];
    if (el) el.focus();
  });
};

const selectTab = index => {
  if (!props.tabs[index] || props.tabs[index].disabled) return;
  emit('tabChanged', props.tabs[index]);
};

const selectAndFocusTab = index => {
  selectTab(index);
  focusTab(index);
};

const enabledPosition = index => enabledIndexes.value.indexOf(index);

const moveFocus = (fromIndex, delta) => {
  if (!enabledIndexes.value.length) return;

  const currentPosition = Math.max(0, enabledPosition(fromIndex));
  const nextPosition =
    (currentPosition + delta + enabledIndexes.value.length) %
    enabledIndexes.value.length;
  selectAndFocusTab(enabledIndexes.value[nextPosition]);
};

const onTabKeydown = (event, index) => {
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      moveFocus(index, 1);
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      moveFocus(index, -1);
      break;
    case 'Home':
      event.preventDefault();
      if (enabledIndexes.value[0] !== undefined) {
        selectAndFocusTab(enabledIndexes.value[0]);
      }
      break;
    case 'End': {
      event.preventDefault();
      const lastIndex = enabledIndexes.value[enabledIndexes.value.length - 1];
      if (lastIndex !== undefined) selectAndFocusTab(lastIndex);
      break;
    }
    case 'Enter':
    case ' ':
      event.preventDefault();
      selectAndFocusTab(index);
      break;
    default:
      break;
  }
};

const showDivider = index => {
  return (
    // Show dividers after the active tab, but not after the last tab
    (index > activeTab.value && index < props.tabs.length - 1) ||
    // Show dividers before the active tab, but not immediately before it and not before the first tab
    (index < activeTab.value - 1 && index > -1)
  );
};
</script>

<template>
  <div
    role="tablist"
    :aria-label="ariaLabel || undefined"
    class="relative flex items-center h-8 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 w-fit transition-all duration-200 ease-out has-[button:active]:scale-[1.01]"
  >
    <div
      class="absolute rounded-lg bg-n-solid-active shadow-sm pointer-events-none h-8 outline-1 outline outline-n-container inset-y-0"
      :class="{ 'transition-all duration-300 ease-out': enableTransition }"
      :style="indicatorStyle"
    />

    <template v-for="(tab, index) in tabs" :key="tabKey(tab, index)">
      <button
        :id="tabId(tab, index)"
        :ref="el => (tabRefs[index] = el)"
        type="button"
        role="tab"
        :aria-selected="activeTab === index ? 'true' : 'false'"
        :aria-controls="panelId(tab, index)"
        :aria-disabled="tab.disabled ? 'true' : undefined"
        :tabindex="activeTab === index ? 0 : -1"
        :disabled="tab.disabled"
        class="relative z-10 px-4 truncate py-1.5 text-sm border-0 outline-1 outline-transparent rounded-lg transition-all duration-200 ease-out hover:text-n-brand active:scale-[1.02]"
        :class="[
          activeTab === index
            ? 'text-n-blue-11 scale-100'
            : 'text-n-slate-10 scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50',
        ]"
        @click="selectTab(index)"
        @keydown="onTabKeydown($event, index)"
      >
        {{ tab.label }} {{ tab.count ? `(${tab.count})` : '' }}
      </button>
      <div
        v-if="index < tabs.length - 1"
        class="w-px h-3.5 rounded my-auto transition-colors duration-300 ease-in-out"
        :class="
          showDivider(index)
            ? 'bg-n-strong'
            : 'bg-transparent dark:bg-transparent'
        "
      />
    </template>
  </div>
</template>
